import { RegionProgress, useAuth, useSSE } from '@/zustand/Zustand'
import { useEffect, useRef } from 'react'

// Re-export for consumers that import from here
export type { RegionProgress }

interface UseUpdateStreamOptions {
  onAuctionUpdate?: (timestamp: number, source?: string) => void
  onTokenUpdate?: (price: number, timestamp: number) => void
  onDataReady?: (isPartial: boolean, cycleId?: string) => void
  enabled?: boolean
}

interface UseUpdateStreamReturn {
  isConnected: boolean
  reconnect: () => void
  progress: RegionProgress | null
  isUpdating: boolean
  partialDataAvailable: boolean
  clearPartialDataAvailable: () => void
}

/**
 * Hook to subscribe to SSE updates.
 *
 * Multiple components can call this hook — they all share ONE EventSource
 * connection managed by the useSSE Zustand store. The connection opens
 * when the first consumer mounts and closes when the last unmounts.
 */
export function useUpdateStream(options: UseUpdateStreamOptions = {}): UseUpdateStreamReturn {
  const { onAuctionUpdate, onTokenUpdate, onDataReady, enabled = true } = options
  const { userData } = useAuth()
  const region = userData?.region

  const { isConnected, progress, isUpdating, partialDataAvailable, clearPartialDataAvailable, acquire, release, reconnect, addListener } = useSSE()

  // Acquire/release the shared connection
  useEffect(() => {
    if (!region || !enabled) return

    acquire(region)
    return () => release()
  }, [region, enabled, acquire, release])

  // Keep callbacks in refs so listener registration is stable
  const onAuctionUpdateRef = useRef(onAuctionUpdate)
  const onTokenUpdateRef = useRef(onTokenUpdate)
  const onDataReadyRef = useRef(onDataReady)

  useEffect(() => { onAuctionUpdateRef.current = onAuctionUpdate }, [onAuctionUpdate])
  useEffect(() => { onTokenUpdateRef.current = onTokenUpdate }, [onTokenUpdate])
  useEffect(() => { onDataReadyRef.current = onDataReady }, [onDataReady])

  // Register event listeners once on mount
  useEffect(() => {
    const unsubs: (() => void)[] = []

    unsubs.push(addListener({
      type: 'auction-update',
      callback: (timestamp: number, source?: string) =>
        onAuctionUpdateRef.current?.(timestamp, source),
    }))

    unsubs.push(addListener({
      type: 'token-update',
      callback: (price: number, timestamp: number) =>
        onTokenUpdateRef.current?.(price, timestamp),
    }))

    unsubs.push(addListener({
      type: 'data-ready',
      callback: (isPartial: boolean, cycleId?: string) =>
        onDataReadyRef.current?.(isPartial, cycleId),
    }))

    return () => unsubs.forEach(fn => fn())
  }, [addListener])

  return {
    isConnected,
    reconnect,
    progress,
    isUpdating,
    partialDataAvailable,
    clearPartialDataAvailable,
  }
}